import type {
  DataGridColumn,
  DataGridColumnLayout,
  DataGridColumnLayoutItem,
  DataGridPinnedSide,
} from './types';

const DEFAULT_COLUMN_WIDTH = 160;
export const DATA_GRID_ROW_NUMBER_WIDTH = 52;

const isVisible = <TRow>(column: DataGridColumn<TRow>): boolean =>
  column.visible !== false;

/**
 * The visible columns split into the three groups the grid draws in order:
 * left-pinned, then regular, then right-pinned. The layout below and
 * {@link getDataGridVisibleColumnOrder} both work from this, so a cell
 * address's `columnIndex` means the same thing wherever it is derived.
 */
const groupByPin = <TRow>(
  columns: DataGridColumn<TRow>[]
): {
  left: DataGridColumn<TRow>[];
  regular: DataGridColumn<TRow>[];
  right: DataGridColumn<TRow>[];
} => {
  const visible = columns.filter(isVisible);
  return {
    left: visible.filter((column) => column.pinned === 'left'),
    regular: visible.filter(
      (column) => column.pinned !== 'left' && column.pinned !== 'right'
    ),
    right: visible.filter((column) => column.pinned === 'right'),
  };
};

/**
 * The visible columns in the order the grid lays them out, which is the order a
 * cell address's `columnIndex` counts in.
 *
 * Exported because addressing a cell by column outside the grid — focusing a
 * freshly added row, say — has to arrive at the same index the layout did.
 */
export function getDataGridVisibleColumnOrder<TRow>(
  columns: DataGridColumn<TRow>[]
): DataGridColumn<TRow>[] {
  const groups = groupByPin(columns);
  return [...groups.left, ...groups.regular, ...groups.right];
}

const widthForColumn = <TRow>(column: DataGridColumn<TRow>): number => {
  const width = column.width ?? DEFAULT_COLUMN_WIDTH;
  const minWidth = column.minWidth ?? 80;
  const maxWidth = column.maxWidth ?? 600;
  return Math.min(maxWidth, Math.max(minWidth, width));
};

const sumWidths = <TRow>(columns: DataGridColumnLayoutItem<TRow>[]): number =>
  columns.reduce((sum, column) => sum + column.width, 0);

const layoutGroup = <TRow>(
  columns: DataGridColumn<TRow>[],
  pinned: DataGridPinnedSide | null,
  startX: number,
  columnIndexOffset: number
): DataGridColumnLayoutItem<TRow>[] => {
  let x = startX;
  return columns.map((column, index) => {
    const width = widthForColumn(column);
    const item: DataGridColumnLayoutItem<TRow> = {
      column,
      x,
      width,
      columnIndex: columnIndexOffset + index,
      pinned,
    };
    x += width;
    return item;
  });
};

/**
 * Grows the regular columns to take up `available`, in proportion to the widths
 * they already have.
 *
 * A grid whose columns do not reach the right edge left a bare strip beside the
 * last one, so the table read as narrower than the toolbar sitting directly
 * above it. Filling that strip with a blank column would only have moved the
 * problem — it looks like a column, and there isn't one. So the columns that do
 * exist stretch to cover it, the way `width: 100%` has always treated a table.
 *
 * Proportional rather than all-to-the-last: giving the remainder to one column
 * makes it wildly wider than its neighbours for no reason a reader can see.
 * Rounding lands on the last column so the widths sum to `available` exactly
 * and no sub-pixel seam is left at the edge.
 */
const stretchToFill = <TRow>(
  columns: DataGridColumnLayoutItem<TRow>[],
  available: number
): DataGridColumnLayoutItem<TRow>[] => {
  const current = sumWidths(columns);
  if (columns.length === 0 || current <= 0 || available <= current) {
    return columns;
  }
  const scale = available / current;
  let x = columns[0]?.x ?? 0;
  return columns.map((item, index) => {
    const width =
      index === columns.length - 1
        ? // Whatever is left, so rounding cannot leave or overshoot a pixel.
          available - (x - (columns[0]?.x ?? 0))
        : Math.round(item.width * scale);
    const stretched = { ...item, x, width };
    x += width;
    return stretched;
  });
};

export function getDataGridColumnLayout<TRow>(
  columns: DataGridColumn<TRow>[],
  rowNumberWidth = DATA_GRID_ROW_NUMBER_WIDTH,
  /**
   * The width the grid has to fill. Columns narrower than this in total are
   * stretched to meet it; wider ones are left alone and scroll as before.
   */
  availableWidth?: number
): DataGridColumnLayout<TRow> {
  const {
    left: leftColumns,
    regular: regularColumns,
    right: rightColumns,
  } = groupByPin(columns);
  const visibleColumnCount =
    leftColumns.length + regularColumns.length + rightColumns.length;

  const leftPinned = layoutGroup(leftColumns, 'left', rowNumberWidth, 0);
  const leftPinnedWidth = rowNumberWidth + sumWidths(leftPinned);
  // Known before the regular columns are laid out, because what those get to
  // stretch into is whatever the pinned groups on either side do not take.
  const rightPinnedWidth = rightColumns.reduce(
    (sum, column) => sum + widthForColumn(column),
    0
  );
  const regular = stretchToFill(
    layoutGroup(regularColumns, null, leftPinnedWidth, leftPinned.length),
    availableWidth === undefined
      ? 0
      : availableWidth - leftPinnedWidth - rightPinnedWidth
  );
  const regularWidth = sumWidths(regular);
  const rightPinned = layoutGroup(
    rightColumns,
    'right',
    leftPinnedWidth + regularWidth,
    leftPinned.length + regular.length
  );

  return {
    rowNumber: {
      id: '__row_number__',
      x: 0,
      width: rowNumberWidth,
      columnIndex: 0,
      pinned: 'left',
    },
    leftPinned,
    regular,
    rightPinned,
    leftPinnedWidth,
    regularWidth,
    rightPinnedWidth,
    totalWidth: leftPinnedWidth + regularWidth + rightPinnedWidth,
    visibleColumns: [...leftPinned, ...regular, ...rightPinned],
    ariaColumnCount: visibleColumnCount + 1,
  };
}
